import {
  Box,
  Card,
  CardContent,
  CardHeader,
  IconButton,
  Input,
  InputAdornment,
  Typography,
  Grid,
  Chip,
  Avatar,
  Stack,
  Paper,
  Container,
  Fade,
  Zoom,
  Button,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
  FormControl,
  Select,
  MenuItem
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { friendModalListApi } from "../../services/AllServices";
import errorHandler from "../../utils/errorHandler";
import { Helmet } from "react-helmet-async";
import CloseIcon from "@mui/icons-material/Close";
import PeopleIcon from "@mui/icons-material/People";
import PersonIcon from "@mui/icons-material/Person";
import ViewListIcon from "@mui/icons-material/ViewList";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import { IMAGE_BASE } from "../../utils/constants";
import DataTable from "react-data-table-component";
import { debounce } from "lodash";
import CardLayout from "../../layouts/CardLayout";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { fDate } from "../../utils/formatTime";
import { capitalizeFirstLetter } from "../../utils/helpers";


const ViewFriendManagement = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tableData, setTableData] = useState([]);
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchItem, setSearchItem] = useState("");
  const [totalFriends, setTotalFriends] = useState(0);
  const [activeFriends, setActiveFriends] = useState(0);
  const [inactiveFriends, setInactiveFriends] = useState(0);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [originalTableData, setOriginalTableData] = useState([]);
  const { id } = useParams();
  

  const userData = async () => {
    try {
      setLoading(true);
      const response = await friendModalListApi.getUserFriendList({
        pageSize: 5,
        pageNumber: 1,
        searchItem: "",
        userId: id,
      });
      
      // Handle the API response structure
      if (response.data && response.data.data) {
        setData(response.data.data.userInfo);
        const friends = response.data.data.relations || [];
        setTableData(friends);
        setOriginalTableData(friends); // Store original data for search
        
        // Calculate statistics
        setTotalFriends(friends.length);
        setActiveFriends(friends.filter(friend => friend.status === 1).length);
        setInactiveFriends(friends.filter(friend => friend.status === 0).length);
      }
    } catch (error) {
      errorHandler(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      userData();
    }
  }, [id]);

    const debouncedSearch = useCallback(
      debounce((query) => {
        // Filter the original data based on search query
        if (query) {
          const filteredFriends = originalTableData.filter(friend => 
            friend.username?.toLowerCase().includes(query.toLowerCase()) ||
            friend.firstname?.toLowerCase().includes(query.toLowerCase()) ||
            friend.lastname?.toLowerCase().includes(query.toLowerCase()) ||
            friend.email?.toLowerCase().includes(query.toLowerCase()) ||
            friend.phoneNumber?.toLowerCase().includes(query.toLowerCase()) ||
            friend.country?.toLowerCase().includes(query.toLowerCase())
          );
          setTableData(filteredFriends);
        } else {
          // Reset to original data when search is cleared
          setTableData(originalTableData);
        }
      }, 400),
      [originalTableData]
    );
  
    const handleSearchChange = (event) => {
      const query = event.target.value;
      setSearchItem(query);
      debouncedSearch(query);
    };

  const handleGoBack = () => {
    const page = searchParams.get('page');
    const view = searchParams.get('view');
    
    if (page && view) {
      navigate(`/friend-management?page=${page}&view=${view}`);
    } else if (page) {
      navigate(`/friend-management?page=${page}`);
    } else if (view) {
      navigate(`/friend-management?view=${view}`);
    } else {
      navigate('/friend-management');
    }
  };

  const renderFriendCard = (friend, index) => (
    <Zoom in={!loading} className={`friend-card-zoom friend-card-zoom-${index}`}>
      <Grid item xs={12} sm={6} md={4} lg={3} key={friend._id}>
        <Paper
          elevation={0}
          sx={{
            p: 3,
            borderRadius: 3,
            background: 'linear-gradient(145deg, rgba(255, 255, 255, 0.03) 0%, rgba(255, 255, 255, 0.05) 100%)',
            border: '1px solid',
            borderColor: 'divider',
            transition: 'all 0.3s ease',
            cursor: 'pointer',
            '&:hover': {
              borderColor: 'primary.main',
              transform: 'translateY(-4px)',
              boxShadow: '0 12px 40px rgba(0, 161, 221, 0.2)',
            }
          }}
        >
          {/* Header */}
          {console.log(friend)}
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
            <Avatar
              src={friend.image ? `${IMAGE_BASE}${friend.image}` : undefined}
              sx={{
                width: 48,
                height: 48,
                background: 'linear-gradient(135deg, #00a1dd 0%, #42a5f5 100%)',
                boxShadow: '0 4px 16px rgba(0, 161, 221, 0.3)'
              }}
            >
            </Avatar>
            <Chip
              label={friend.statusText || "Unknown"}
              size="small"
              sx={{
                backgroundColor: friend.status === 1 
                  ? 'rgba(76, 175, 80, 0.2)' 
                  : 'rgba(244, 67, 54, 0.2)',
                color: friend.status === 1 ? '#4caf50' : '#f44336',
                fontWeight: 600,
                border: '1px solid',
                borderColor: friend.status === 1 
                  ? 'rgba(76, 175, 80, 0.3)' 
                  : 'rgba(244, 67, 54, 0.3)'
              }}
            />
          </Stack>

          {/* Content */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ color: 'white', fontWeight: 700, mb: 1 }}>
              {friend.username || 'N/A'}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
              {friend.firstname && friend.lastname ? `${friend.firstname} ${friend.lastname}` : 'N/A'}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
              {friend.email || 'N/A'}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
              {friend.phoneNumber || 'N/A'}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
              {friend.country || 'N/A'}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block' }}>
              Relationship: {friend.relationshipStatus || 'N/A'}
            </Typography>
          </Box>
        </Paper>
      </Grid>
    </Zoom>
  );

  const columns = [
    {
      name: "S.No.",
      cell: (row, index) => (currentPage - 1) * pageSize + index + 1,
      width: "80px",
    },
    {
      name: "Avatar",
      cell: (row) => (
        <Avatar
          src={row.image ? `${IMAGE_BASE}${row.image}` : undefined}
          alt="avatar"
          sx={{ 
            width: 40, 
            height: 40,
            background: 'linear-gradient(135deg, #00a1dd 0%, #42a5f5 100%)'
          }}
        >
          <PersonIcon sx={{ fontSize: 20, color: 'white' }} />
        </Avatar>
      ),
      width: "80px",
    },
    {
      name: "User Name",
      selector: (row) => {
        return row.username || "-";
      },
      sortable: true,
    },
    {
      name: "Full Name",
      selector: (row) => {
        return row.firstname && row.lastname ? `${row.firstname} ${row.lastname}` : "-";
      },
      sortable: true,
    },
    {
      name: "Email",
      selector: (row) => row.email || "-",
      sortable: true,
    },
    {
      name: "Phone",
      selector: (row) => row.phoneNumber || "-",
      sortable: true,
    },
    {
      name: "Country",
      selector: (row) => row.country || "-",
      sortable: true,
    },
    {
      name: "Status",
      cell: (row) => (
        <Chip
          label={row.statusText || "Unknown"}
          size="small"
          sx={{
            backgroundColor: row.status === 1 
              ? 'rgba(76, 175, 80, 0.2)' 
              : 'rgba(244, 67, 54, 0.2)',
            color: row.status === 1 ? '#4caf50' : '#f44336',
            fontWeight: 600,
            border: '1px solid',
            borderColor: row.status === 1 
              ? 'rgba(76, 175, 80, 0.3)' 
              : 'rgba(244, 67, 54, 0.3)'
          }}
        />
      ),
      sortable: true,
    },
    {
      name: "Relationship",
      selector: (row) => row.relationshipStatus || "-",
      sortable: true,
    },
  ];

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Helmet>
        <title>Friend Details - {data?.username || 'User'}</title>
      </Helmet>
      <Fade in timeout={500}>
        <Stack spacing={4}>
          {/* Header Section */}
          <Paper
            elevation={0}
            sx={{
              p: 4,
              borderRadius: 3,
              background: 'linear-gradient(135deg, #1a202c 0%, #2d3748 100%)',
              border: '1px solid',
              borderColor: 'divider',
              position: 'relative',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '4px',
                background: 'linear-gradient(90deg, #00a1dd 0%, #42a5f5 50%, #90caf9 100%)',
              }
            }}
          >
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
              <Stack direction="row" alignItems="center" spacing={3}>
                <Avatar
                  src={data?.image ? `${IMAGE_BASE}${data.image}` : undefined}
                  sx={{
                    width: 80,
                    height: 80,
                    background: 'linear-gradient(135deg, #00a1dd 0%, #42a5f5 100%)',
                    boxShadow: '0 8px 32px rgba(0, 161, 221, 0.4)'
                  }}
                >
                  <PersonIcon sx={{ fontSize: 40, color: 'white' }} />
                </Avatar>
                <Box>
                  <Typography variant="h4" sx={{ color: 'white', fontWeight: 700, mb: 1 }}>
                    {capitalizeFirstLetter(data?.firstname , data?.lastname) || 'User'} - Friends
                  </Typography>
                  <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    View and manage all friends of this user
                  </Typography>
                </Box>
              </Stack>
              <Button
                variant="contained"
                onClick={handleGoBack}
                sx={{
                  background: 'linear-gradient(135deg, #00a1dd 0%, #42a5f5 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #0091c8 0%, #3b8fd9 100%)',
                  }
                }}
              >
                Back to Friends
              </Button>
            </Stack>

            {/* Stats Cards */}
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={3}>
                <Paper
                  sx={{
                    p: 3,
                    borderRadius: 2,
                    background: 'rgba(0, 161, 221, 0.1)',
                    border: '1px solid rgba(0, 161, 221, 0.2)',
                    textAlign: 'center'
                  }}
                >
                  <Typography variant="h4" sx={{ color: 'primary.main', fontWeight: 700, mb: 1 }}>
                    {totalFriends}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                    Total Friends
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Paper
                  sx={{
                    p: 3,
                    borderRadius: 2,
                    background: 'rgba(76, 175, 80, 0.1)',
                    border: '1px solid rgba(76, 175, 80, 0.2)',
                    textAlign: 'center'
                  }}
                >
                  <Typography variant="h4" sx={{ color: '#4caf50', fontWeight: 700, mb: 1 }}>
                    {activeFriends}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                    Active Friends
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Paper
                  sx={{
                    p: 3,
                    borderRadius: 2,
                    background: 'rgba(244, 67, 54, 0.1)',
                    border: '1px solid rgba(244, 67, 54, 0.2)',
                    textAlign: 'center'
                  }}
                >
                  <Typography variant="h4" sx={{ color: '#f44336', fontWeight: 700, mb: 1 }}>
                    {inactiveFriends}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                    Inactive Friends
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Paper
                  sx={{
                    p: 3,
                    borderRadius: 2,
                    background: 'rgba(156, 39, 176, 0.1)',
                    border: '1px solid rgba(156, 39, 176, 0.2)',
                    textAlign: 'center'
                  }}
                >
                  <Typography variant="h4" sx={{ color: '#9c27b0', fontWeight: 700, mb: 1 }}>
                    {tableData?.length || 0}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                    Total Relations
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
          </Paper>


          {/* Friends List Section */}
          <Paper
            elevation={0}
        sx={{
              borderRadius: 3,
              background: 'rgba(255, 255, 255, 0.02)',
              border: '1px solid',
              borderColor: 'divider',
              overflow: 'hidden'
            }}
          >
            {/* Search Controls */}
            <Box sx={{ p: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ flexWrap: 'wrap', gap: 2 }}>
                <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
                  Friends List
                </Typography>
                <Stack direction="row" alignItems="center" spacing={2} sx={{ flexWrap: 'wrap', gap: 2 }}>
        <Input
          value={searchItem}
                    placeholder="Search friends..."
          onChange={handleSearchChange}
                    sx={{
                      color: 'white',
                      '& .MuiInput-input': {
                        color: 'white',
                      },
                      '& .MuiInput-input::placeholder': {
                        color: 'rgba(255, 255, 255, 0.5)',
                        opacity: 1,
                      },
                      '& .MuiInput-root': {
                        backgroundColor: 'rgba(255, 255, 255, 0.05)',
                        borderRadius: 2,
                        padding: '8px 16px',
                      }
                    }}
          endAdornment={
            <InputAdornment position="end">
              <IconButton
                aria-label="clear-search"
                onClick={() => {
                  const cleared = "";
                  setSearchItem(cleared);
                  debouncedSearch(cleared);
                }}
                          sx={{ color: 'text.secondary' }}
              >
                <CloseIcon />
              </IconButton>
            </InputAdornment>
          }
        />
                  <ToggleButtonGroup
                    value={viewMode}
                    exclusive
                    onChange={(e, newMode) => {
                      if (newMode) {
                        setViewMode(newMode);
                      }
                    }}
                    sx={{
                      '& .MuiToggleButton-root': {
                        color: 'text.secondary',
                        borderColor: 'divider',
                        '&.Mui-selected': {
                          backgroundColor: 'primary.main',
                          color: 'white',
                          '&:hover': {
                            backgroundColor: 'primary.dark',
                          }
                        }
                      }
                    }}
                  >
                    <ToggleButton value="grid">
                      <ViewModuleIcon />
                    </ToggleButton>
                    <ToggleButton value="list">
                      <ViewListIcon />
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Stack>
              </Stack>
            </Box>

            {/* Content Section */}
            {viewMode === 'grid' ? (
              /* Grid View */
              <Box sx={{ p: 3 }}>
                {loading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                    <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                      Loading friends...
                    </Typography>
                  </Box>
                ) : !tableData || tableData.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 8 }}>
                    <Avatar
                      sx={{
                        width: 80,
                        height: 80,
                        mx: 'auto',
                        mb: 3,
                        background: 'linear-gradient(135deg, #9c27b0 0%, #e91e63 100%)',
                        boxShadow: '0 8px 32px rgba(156, 39, 176, 0.3)'
                      }}
                    >
                      <PeopleIcon sx={{ fontSize: 40 }} />
                    </Avatar>
                    <Typography variant="h6" color="text.secondary" sx={{ mb: 2, fontWeight: 600 }}>
                      No friends found
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      This user doesn't have any friends yet
                    </Typography>
                  </Box>
                ) : (
                  <>
                    <Grid container spacing={3}>
                      {tableData.map((friend, index) => renderFriendCard(friend, index))}
                    </Grid>
                    
                    {/* Pagination Controls (Grid View) */}
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center', 
                      gap: 2,
                      flexWrap: 'wrap',
                      mt: 4,
                      p: 2,
                      borderRadius: 2,
                      background: 'rgba(255, 255, 255, 0.02)',
                      border: '1px solid',
                      borderColor: 'divider'
                    }}>
                      {/* Left side - Page Size Selector */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" sx={{ color: 'white', whiteSpace: 'nowrap' }}>
                          Items per page:
                        </Typography>
                        <FormControl size="small">
                          <Select
                            value={pageSize}
                            onChange={(e) => {
                              setPageSize(e.target.value);
                              setCurrentPage(1);
                            }}
                            displayEmpty
                            sx={{
                              color: 'white',
                              minWidth: 80,
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'rgba(255, 255, 255, 0.3)',
                              },
                              '&:hover .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'rgba(255, 255, 255, 0.5)',
                              },
                              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'rgba(255, 255, 255, 0.7)',
                              },
                              '& .MuiSvgIcon-root': {
                                color: 'white',
                              },
                            }}
                          >
                            <MenuItem value={2}>2</MenuItem>
                            <MenuItem value={5}>5</MenuItem>
                            <MenuItem value={10}>10</MenuItem>
                            <MenuItem value={20}>20</MenuItem>
                            <MenuItem value={50}>50</MenuItem>
                          </Select>
                        </FormControl>
                      </Box>

                      {/* Right side - Navigation Controls */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Button
                          variant="outlined"
                          size="small"
                          disabled={currentPage === 1}
                          onClick={() => setCurrentPage(currentPage - 1)}
                          sx={{ color: 'white', borderColor: 'rgba(255, 255, 255, 0.3)' }}
                        >
                          Previous
                        </Button>
                        <Typography variant="body2" sx={{ color: 'white', mx: 1 }}>
                          Page {currentPage} of {Math.ceil(tableData.length / pageSize)}
                        </Typography>
                        <Button
                          variant="outlined"
                          size="small"
                          disabled={currentPage >= Math.ceil(tableData.length / pageSize)}
                          onClick={() => setCurrentPage(currentPage + 1)}
                          sx={{ color: 'white', borderColor: 'rgba(255, 255, 255, 0.3)' }}
                        >
                          Next
                        </Button>
                      </Box>
                    </Box>
                  </>
                )}
      </Box>
            ) : (
              /* List View */
              <Box>
              <DataTable
                columns={columns}
                data={tableData || []}
                pagination
                paginationServer={false}
                paginationPerPage={pageSize}
                paginationTotalRows={tableData.length}
                onChangePage={(page) => setCurrentPage(page)}
                onChangeRowsPerPage={(newPageSize) => setPageSize(newPageSize)}
                paginationRowsPerPageOptions={[10, 20, 50]}
                noDataComponent={
                    <Box sx={{ textAlign: 'center', py: 8 }}>
                      <Avatar
                        sx={{
                          width: 80,
                          height: 80,
                          mx: 'auto',
                          mb: 3,
                          background: 'linear-gradient(135deg, #9c27b0 0%, #e91e63 100%)',
                          boxShadow: '0 8px 32px rgba(156, 39, 176, 0.3)'
                        }}
                      >
                        <PeopleIcon sx={{ fontSize: 40 }} />
                      </Avatar>
                      <Typography variant="h6" color="text.secondary" sx={{ mb: 2, fontWeight: 600 }}>
                        No friends found
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        This user doesn't have any friends yet
                  </Typography>
                    </Box>
                }
                customStyles={{
                  headCells: {
                    style: {
                      fontSize: "15px",
                      fontWeight: "bold",
                        backgroundColor: 'rgba(255, 255, 255, 0.02)',
                        borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                        color: 'white'
                      },
                    },
                    rows: {
                      style: {
                        backgroundColor: 'transparent',
                        color: 'white',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.02)',
                        }
                      }
                    },
                    cells: {
                      style: {
                        color: 'white',
                      }
                    }
                  }}
                />
              </Box>
            )}
          </Paper>
        </Stack>
      </Fade>
    </Container>
  );
};

export default ViewFriendManagement;

